import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip';
import { cn } from '../cn.js';

/**
 * A tooltip.
 *
 * It supplements a control that already has an accessible name; it never carries the only copy of
 * information, because a touch user cannot hover and a screen-reader user may not reach it.
 */
export const TooltipProvider = BaseTooltip.Provider;
export const TooltipRoot = BaseTooltip.Root;
export const TooltipTrigger = BaseTooltip.Trigger;

export function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: BaseTooltip.Popup.Props & { sideOffset?: number }) {
  return (
    <BaseTooltip.Portal>
      <BaseTooltip.Positioner sideOffset={sideOffset}>
        <BaseTooltip.Popup
          className={cn(
            'z-(--elevation-popover) max-w-72',
            'rounded-(--radius-md) px-(--space-sm) py-(--space-xs)',
            'bg-(--color-surface-inverse) text-(--color-text-inverse)',
            'text-(length:--font-size-caption) shadow-(--shadow-popover)',
            'transition-opacity duration-(--motion-duration-fast) ease-(--motion-easing-standard)',
            'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            'motion-reduce:transition-none',
            className,
          )}
          {...props}
        >
          {children}
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
}

/** The common case: a trigger and a line of text. */
export function Tooltip({
  content,
  children,
  ...rootProps
}: BaseTooltip.Root.Props & { content: React.ReactNode }) {
  return (
    <BaseTooltip.Root {...rootProps}>
      <BaseTooltip.Trigger render={children as React.ReactElement<Record<string, unknown>>} />
      <TooltipContent>{content}</TooltipContent>
    </BaseTooltip.Root>
  );
}
